import React from 'react';
import { motion } from 'framer-motion';
import useQuestionStore from '../stores/questionStore';
import useProgressStore from '../stores/progressStore';

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export default function Stats() {
  const stats = useQuestionStore((s) => s.getStats());
  const { streak, examResults, dailyCounts, dailyGoal } = useProgressStore();

  const chartData = Object.entries(stats.byCategory)
    .filter(([, v]) => v.total > 0)
    .map(([name, v]) => ({
      name: name.replace('Endodontic ', '').replace('Root Canal ', 'RC '),
      mastery: v.mastery,
      correct: v.correct,
      total: v.total,
    }))
    .sort((a, b) => a.mastery - b.mastery);

  // Last 7 days activity
  const last7 = [...Array(7)].map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    const counts = dailyCounts[key] || { questionsAnswered: 0, correctAnswers: 0 };
    return {
      label: d.toLocaleDateString('en-US', { weekday: 'short' }),
      ...counts,
      pct: counts.questionsAnswered > 0
        ? Math.round((counts.correctAnswers / counts.questionsAnswered) * 100)
        : 0,
    };
  });

  const maxAnswered = Math.max(...last7.map((d) => d.questionsAnswered), dailyGoal, 1);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <motion.div
        className="max-w-5xl mx-auto space-y-6"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        <motion.div variants={cardVariants}>
          <h1 className="text-2xl font-bold text-white">Stats &amp; Progress</h1>
          <p className="text-slate-400 text-sm mt-1">Your full performance breakdown</p>
        </motion.div>

        {/* Summary stats — top of page */}
        <motion.div variants={cardVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Attempted', value: stats.attempted, icon: '📝' },
            { label: 'Overall Accuracy', value: `${stats.percentCorrect}%`, icon: '🎯' },
            { label: 'Study Streak', value: `${streak}d 🔥`, icon: '🔥' },
            { label: 'Exams Taken', value: examResults.length, icon: '🧪' },
          ].map(({ label, value }) => (
            <div key={label} className="card p-5 text-center">
              <p className="text-2xl font-bold text-white">{value}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </div>
          ))}
        </motion.div>

        {/* Mastery by Category — horizontal bars */}
        <motion.div variants={cardVariants} className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide mb-1">Mastery by Category</h2>
          <p className="text-xs text-slate-500 mb-4">
            Green ≥75% · Amber 50–74% · Red &lt;50% · (requires ≥3 answered)
          </p>
          {chartData.filter(d => d.total > 0).length > 0 ? (
            <div className="space-y-3">
              {chartData
                .filter(d => d.total > 0)
                .sort((a, b) => a.mastery - b.mastery)
                .map(({ name, mastery, correct, total }) => {
                  const hasData = total >= 3;
                  const barColor = !hasData ? 'bg-slate-600'
                    : mastery >= 75 ? 'bg-emerald-500'
                    : mastery >= 50 ? 'bg-amber-500'
                    : 'bg-red-500';
                  const textColor = !hasData ? 'text-slate-500'
                    : mastery >= 75 ? 'text-emerald-400'
                    : mastery >= 50 ? 'text-amber-400'
                    : 'text-red-400';
                  return (
                    <div key={name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-300 flex-1 truncate">{name}</span>
                        <span className={`text-xs font-semibold ml-2 ${textColor}`}>
                          {hasData ? `${correct}/${total} (${mastery}%)` : `${correct}/${total} (–)`}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: hasData ? `${mastery}%` : `${Math.round((correct / Math.max(total, 1)) * 100)}%` }}
                          transition={{ duration: 0.6, ease: 'easeOut' }}
                          className={`h-full rounded-full ${barColor}`}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-6">Answer some questions to see mastery data.</p>
          )}
        </motion.div>

        {/* Last 7 days bar chart */}
        <motion.div variants={cardVariants} className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white uppercase tracking-wide mb-5">
            Last 7 Days
          </h2>
          <div className="flex items-end gap-2 h-32">
            {last7.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-slate-500">{day.questionsAnswered || ''}</span>
                <div className="w-full bg-slate-700 rounded-t-md relative overflow-hidden" style={{ height: '80px' }}>
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-teal-500 rounded-t-md transition-all duration-700"
                    style={{ height: `${(day.questionsAnswered / maxAnswered) * 100}%` }}
                  />
                  {/* Goal line */}
                  <div
                    className="absolute left-0 right-0 h-px bg-amber-400/50"
                    style={{ bottom: `${(dailyGoal / maxAnswered) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500">{day.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-3">Gold line = daily goal ({dailyGoal} questions)</p>
        </motion.div>

        {/* Exam history */}
        {examResults.length > 0 && (
          <motion.div variants={cardVariants} className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-white uppercase tracking-wide mb-4">Exam History</h2>
            <div className="space-y-2">
              {examResults.slice(0, 10).map((r) => (
                <div key={r.id} className="flex items-center gap-3 text-sm py-2 border-b border-slate-700/50 last:border-0">
                  <span className="text-slate-400 w-28 truncate">{r.examType}</span>
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${Math.round((r.score / r.total) * 100) >= 75 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.round((r.score / r.total) * 100)}%` }}
                    />
                  </div>
                  <span className={`font-semibold w-12 text-right ${Math.round((r.score / r.total) * 100) >= 75 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {Math.round((r.score / r.total) * 100)}%
                  </span>
                  <span className="text-slate-500 w-20 text-right text-xs">
                    {new Date(r.date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
